import "./App.css";

//PACKAGES
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { PostHogProvider, usePostHog } from "posthog-js/react";

//SCREENS
import PageRouter from "./lzapp_web_common_code/components/Router";
import { AppProvider } from "./lzapp_web_common_code/context/AppProvider";
import { AuthProvider } from "./lzapp_web_common_code/context/AuthProvider";
import { BundleProvider } from "./lzapp_web_common_code/context/BundleProvider";
import { ContentProvider } from "./lzapp_web_common_code/context/ContentProvider";
import { AnalyticsProvider } from "./lzapp_web_common_code/context/AnalyticsProvider";
import { ThemeProvider } from "./lzapp_web_common_code/context/ThemeProvider";
import { useEffect, useState } from "react";
import React from "react";
import { APP_BUNDLES } from "./app_constant";
import { USER_DEAFULT_KEY } from "./lzapp_web_common_code/constant/event";
import Loader from "./lzapp_web_common_code/components/loader";
import MobileAppBanner from "./lzapp_web_common_code/components/common/MobileAppBanner";
import { normalizePromoCode } from "@learnzapp/core/pricing";
import { registerBaseSuperProps } from "./lzapp_web_common_code/utils/analyticsSuperProps";

// Per-page-session caps on outbound $exception events. Module-scope so they
// survive re-renders and persist for the page lifetime; reset on full reload.
// Prevents a stuck tab from blasting thousands of identical events even when
// its bundle pre-dates our benign-exception filters.
const _lzExceptionCounts = new Map();
let _lzExceptionTotal = 0;
const PER_FINGERPRINT_EXCEPTION_CAP = 25;
const TOTAL_EXCEPTION_CAP = 100;

// React error boundary that normalizes non-Error throws so PostHog populates
// $exception_type and $exception_message (read from err.name / err.message).
class NormalizingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const err = error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : JSON.stringify(error));
    const ph = this.props.posthog;
    if (ph && typeof ph.captureException === 'function') {
      ph.captureException(err, { react_component_stack: info?.componentStack });
    }
  }

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallback;
      return Fallback ? <Fallback /> : null;
    }
    return this.props.children;
  }
}

const PostHogBoundary = ({ children, fallback }) => {
  const posthog = usePostHog();
  return (
    <NormalizingErrorBoundary posthog={posthog} fallback={fallback}>
      {children}
    </NormalizingErrorBoundary>
  );
};

// Error fallback UI for the boundary
const ErrorFallback = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    color: '#374151'
  }}>
    <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
      Something went wrong
    </h1>
    <p style={{ marginBottom: '2rem', color: '#6b7280' }}>
      We're sorry, but something went wrong. Please refresh the page and try again.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '0.75rem 1.5rem',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontSize: '1rem'
      }}
    >
      Refresh Page
    </button>
  </div>
);

function App() {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Function to get query parameters
    const getQueryParams = () => {
        const params = new URLSearchParams(window.location.search);
        return {
            promo_code: params.get('promo_code')
        };
    };

    // Capture the promo code from the URL. Normalize before storing —
    // URLSearchParams decodes "?promo_code=LINKEDIN25+" to a trailing space, so
    // the raw value would never match the code map on the paywall (silent
    // no-apply, and the churned-user win-back gate would also skip). See
    // @learnzapp/core/pricing normalizePromoCode.
    const queryParams = getQueryParams();
    const normalizedPromo = normalizePromoCode(queryParams.promo_code);
    if (normalizedPromo) {
        localStorage.setItem('promo_code', normalizedPromo);
    }
  }, []);

  useEffect(() => {}, [localStorage.getItem("SAVED_BUNDLE_DETAILS")]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Theme colors are sourced from @learnzapp/core/theme (via ThemeProvider);
        // no per-bundle override needed. Only show loader for actual initialization time.
        setLoading(false);
      } catch (error) {
        console.error('App initialization error:', error);
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_KEY}
      options={{
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
        capture_pageview: false,
        capture_pageleave: false,
        autocapture: false,
        capture_performance: false,
        // Belt-and-braces deny-list: drops deprecated events that may still fire
        // from cached bundles or long-lived tabs after deploy. Mirrors mobile.
        before_send: (event) => {
          if (!event) return event;
          const deprecated = new Set([
            'question_answered',
            'study_question_viewed',
            'flashcard_answered',
            'flashcard_viewed',
            'user_action',
            'feature_used',
          ]);
          if (deprecated.has(event.event)) return null;

          // Drop benign, non-actionable exceptions so real defects stay visible.
          if (event.event === '$exception') {
            // Hard cap per page session so a stuck tab can't spam PostHog
            // even on bundles that pre-date the filters below.
            const fp = event.properties?.$exception_proposed_fingerprint
              || event.properties?.$exception_fingerprint
              || 'unknown';
            const n = (_lzExceptionCounts.get(fp) || 0) + 1;
            _lzExceptionCounts.set(fp, n);
            _lzExceptionTotal += 1;
            if (n > PER_FINGERPRINT_EXCEPTION_CAP || _lzExceptionTotal > TOTAL_EXCEPTION_CAP) return null;

            const ex = event.properties?.$exception_list?.[0];
            const value = ex?.value || '';
            const type = ex?.type || '';
            if (type === 'DOMException' && /AbortError|aborted a request/i.test(value)) return null;
            if (type === 'DOMException' && /Connection to Indexed Database server lost/i.test(value)) return null;
            if (type === 'FirebaseError' && /client is offline/i.test(value)) return null;
            if (type === 'Error' && /"errorCode"\s*:\s*-999/.test(value)) return null;
            if (type === 'Error' && (value === '{}' || value === '' || value === 'null')) return null;
          }
          return event;
        },
        loaded: (posthog) => {
          // Register super properties the moment the client is initialised.
          // PostHogProvider runs init() in its own effect, AFTER the child
          // AnalyticsProvider effects (whose register() calls no-op against an
          // uninitialised client), so this is the registration that sticks on
          // a page load. Shared helper: reads the real saved-exam key.
          // Isolated so a failure here can never stop the unhandledrejection
          // listener below from being installed.
          try {
            registerBaseSuperProps(posthog);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Analytics] super-property registration failed:', err);
          }

          // Catch async rejections the React error boundary misses.
          // Normalize non-Error reasons so $exception_type/$exception_message
          // populate (PostHog reads from err.name / err.message).
          if (typeof window !== 'undefined' && !window.__lz_unhandledrejection_bound) {
            window.__lz_unhandledrejection_bound = true;
            window.addEventListener('unhandledrejection', (e) => {
              const reason = e?.reason;
              if (reason?.name === 'AbortError') return;
              if (reason?.name === 'ChunkLoadError' && !sessionStorage.getItem('lz_chunk_reload')) {
                sessionStorage.setItem('lz_chunk_reload', '1');
                window.location.reload();
                return;
              }
              // Skip empty/useless rejection reasons — `Promise.reject({})` and
              // friends produce Error("{}") which is unactionable noise.
              if (reason == null) return;
              if (typeof reason === 'object' && !(reason instanceof Error)) {
                try {
                  if (Object.keys(reason).length === 0) return;
                } catch (_) { /* fall through */ }
              }
              const err = reason instanceof Error
                ? reason
                : new Error(typeof reason === 'string' ? reason : JSON.stringify(reason));
              posthog.captureException(err);
            });
          }
        },
      }}
    >
      <BundleProvider>
        <ContentProvider>
          <AuthProvider>
            <ThemeProvider>
              <AppProvider>
                <BrowserRouter>
                  <HelmetProvider>
                    <AnalyticsProvider>
                      <PostHogBoundary fallback={ErrorFallback}>
                        <MobileAppBanner />
                        {loading ? <Loader /> : <PageRouter />}
                      </PostHogBoundary>
                    </AnalyticsProvider>
                  </HelmetProvider>
                </BrowserRouter>
              </AppProvider>
            </ThemeProvider>
          </AuthProvider>
        </ContentProvider>
      </BundleProvider>
    </PostHogProvider>
  );
}

export default App;
